import type { Theme } from '@mui/material'
import {
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  MenuList,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import type { SxProps } from '@mui/system'
import { Box } from '@mui/system'
import { Favorite, Home, Search } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useSearchDialog } from '@tunasong/search'
import { ShortcutBox, UserAvatar, VBox, shortcuts, useFavorites, useHover, useNavigate } from '@tunasong/ui-lib'
import { useRef, type FC } from 'react'
import { ToolsMenuItems } from './tools-menu-items.js'

export interface AppSideBarProps {
  entity?: Persisted<Entity>
}

export const AppSideBar: FC<AppSideBarProps> = props => {
  const { entity } = props

  const { favorites } = useFavorites()

  const { userId } = useCurrentUser()

  const theme = useTheme()
  const avatarSize = useMediaQuery(theme.breakpoints.down('sm')) ? 'tiny' : 'small'

  const { dialog: searchDialog, setOpen: setSearchOpen, open: searchOpen } = useSearchDialog(entity)

  const navigate = useNavigate()
  const menuItemSx: SxProps<Theme> = {
    '&:hover': {
      backgroundColor: theme => theme.vars.palette.action.hover,
    },
  }

  const ref = useRef<HTMLDivElement>(null)
  const hover = useHover(ref.current)
  const color = 'action.active' //  hover ? 'action.active' : 'action.selected'

  // const handleLayout = (layoutName: string) => (ev: MouseEvent) => {
  //   ev.preventDefault()
  //   setLayout(layoutName)
  // }

  return (
    <VBox
      ref={ref}
      sx={{
        overflow: 'auto',
        mt: 1,
        backgroundColor: theme => theme.vars.palette.layers,
        borderRight: theme => `solid 1px ${theme.vars.palette.divider}`,
        color,
        transition: 'all 0.25s',
        zIndex: 1,
        width: 192,
      }}
    >
      <Box sx={{ ml: 2, display: 'flex' }}>
        <UserAvatar size={avatarSize} userId={userId} aria-label="user" showName={true} />
      </Box>

      <MenuList sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <MenuItem onClick={() => navigate('/app')} sx={menuItemSx} selected={location.pathname === '/app'}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Home />
          </ListItemIcon>
          <ListItemText>Home</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setSearchOpen(true)} sx={menuItemSx} selected={searchOpen}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Search />
          </ListItemIcon>
          <ListItemText>Search</ListItemText>
          <ListItemSecondaryAction>
            <ShortcutBox shortcut={shortcuts.Search} showDescription={false} />
          </ListItemSecondaryAction>
        </MenuItem>

        <MenuItem disabled>
          <Typography variant="caption">Tools</Typography>
        </MenuItem>

        <ToolsMenuItems />

        <MenuItem disabled sx={{ flex: 1 }} />

        <MenuItem disabled sx={{ display: 'flex' }}>
          <ListItemIcon>
            <Favorite sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <Typography variant="caption">Favorites</Typography>
        </MenuItem>

        {favorites.map(favorite => (
          <MenuItem key={favorite.path} sx={menuItemSx} onClick={() => navigate(favorite.path)}>
            <ListItemIcon sx={{ color: theme => theme.vars.palette.action.disabled }}>
              <Box sx={{ color: hover ? favorite.color : 'inherit' }}>
                <favorite.Icon />
              </Box>
            </ListItemIcon>
            <ListItemText>
              <Typography variant="caption">{favorite.name}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
        {favorites.length === 0 ? (
          <MenuItem disabled>
            <ListItemText>
              <Typography variant="caption">No favorites</Typography>
            </ListItemText>
          </MenuItem>
        ) : null}
      </MenuList>

      {searchDialog}
    </VBox>
  )
}
